/**
 * Map Container - 프로토타입용 간단한 지도 컴포넌트
 * 실제 Google Maps 대신 시각화된 지도 표시
 */

import { motion } from 'framer-motion';
import { HiLocationMarker } from 'react-icons/hi';

export default function MapContainer({ spots = [], selectedSpot, onSpotClick }) {
  // 제주도 중심 좌표 (대략적인 범위)
  const centerLat = 33.5;
  const centerLng = 126.5;
  const mapWidth = 100;
  const mapHeight = 100;

  // 좌표를 지도 픽셀 좌표로 변환 (간단한 변환)
  const latToY = (lat) => {
    const minLat = 33.0;
    const maxLat = 34.0;
    return ((maxLat - lat) / (maxLat - minLat)) * mapHeight;
  };

  const lngToX = (lng) => {
    const minLng = 126.0;
    const maxLng = 127.0;
    return ((lng - minLng) / (maxLng - minLng)) * mapWidth;
  };

  return (
    <div className="relative w-full h-full rounded-xl overflow-hidden border-2 shadow-lg" style={{ borderColor: 'var(--color-primary)' }}>
      {/* 지도 배경 */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 50%, #7dd3fc 100%)',
        }}
      >
        {/* 그리드 패턴 */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(var(--color-primary) 1px, transparent 1px),
              linear-gradient(90deg, var(--color-primary) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px',
          }}
        />
      </div>

      {/* 마커들 */}
      {spots.map((spot) => {
        const x = lngToX(spot.longitude);
        const y = latToY(spot.latitude);
        const isSelected = selectedSpot?.id === spot.id;

        return (
          <motion.div
            key={spot.id}
            className="absolute cursor-pointer z-10"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              transform: 'translate(-50%, -50%)',
            }}
            initial={{ scale: 0 }}
            animate={{ scale: isSelected ? 1.3 : 1 }}
            whileHover={{ scale: 1.2 }}
            onClick={() => onSpotClick?.(spot)}
          >
            <div
              className="relative"
            >
              {/* 마커 아이콘 */}
              <HiLocationMarker
                className={`w-8 h-8 drop-shadow-lg ${
                  isSelected ? 'text-red-500' : 'text-blue-500'
                }`}
              />
              {/* 펄스 효과 */}
              {isSelected && (
                <motion.div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: 'var(--color-primary)',
                    opacity: 0.3,
                  }}
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.3, 0, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                  }}
                />
              )}
              {/* 스팟 이름 툴팁 */}
              <motion.div
                className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 rounded text-xs font-semibold whitespace-nowrap shadow-lg"
                style={{
                  background: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  border: '1px solid var(--color-primary)',
                }}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: isSelected ? 1 : 0, y: isSelected ? 0 : 5 }}
              >
                {spot.spot_name}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent" style={{ borderTopColor: 'var(--color-surface)' }} />
              </motion.div>
            </div>
          </motion.div>
        );
      })}

      {/* 범례 */}
      <div className="absolute bottom-4 left-4 px-4 py-2 rounded-lg shadow-lg backdrop-blur-md border" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'var(--color-primary)' }}>
        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text)' }}>
          <HiLocationMarker className="w-4 h-4 text-blue-500" />
          <span>스팟 위치</span>
        </div>
      </div>

      {/* 지도 정보 */}
      <div className="absolute top-4 right-4 px-4 py-2 rounded-lg shadow-lg backdrop-blur-md border text-xs" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'var(--color-primary)', color: 'var(--color-text-secondary)' }}>
        프로토타입 지도 (실제 지도 아님)
      </div>
    </div>
  );
}
